import { httpFetchUsers } from "api/admin/user";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import { Select } from "components/misc";
import Pagination from "components/ui/Pagination";
import Search from "components/ui/Search";
import Table from "components/ui/Table";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import UserInfo from "./UserInfo";

const countryLabels: Record<string, string> = {
  NG: "Nigeria",
  GH: "Ghana",
  KE: "Kenya",
};

const otcStatusLabels: Record<string, string> = {
  not_started: "Not Applied",
  draft: "Not Applied",
  pending_review: "Pending",
  under_review: "Pending",
  approved: "Approved",
  declined: "Rejected",
};

const statusClassNames: Record<string, string> = {
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-700",
  pending_review: "bg-yellow-100 text-yellow-700",
  under_review: "bg-yellow-100 text-yellow-700",
  draft: "bg-gray-100 text-gray-600",
  not_started: "bg-gray-100 text-gray-600",
};

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "text-gray-700 bg-gray-100",
}) => (
  <span
    className={`inline-flex rounded px-2 py-1 text-xs font-medium ${className}`}
  >
    {children}
  </span>
);

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { user: selectedUserID } = useParams();
  const [isPanelOpen, setIsOpen] = useState(false);
  const [selectedUser, setUser] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState("A-Z");
  const [searchParams, setSearchParams] = useSearchParams();
  const kycReviewStatus = searchParams.get("kycReviewStatus") || undefined;
  const limit = 50;

  const {
    data: { users, total, page },
    refetch,
  } = useQuery(
    ["users", currentPage, order, kycReviewStatus],
    () => httpFetchUsers(limit, currentPage, order, kycReviewStatus),
    {
      initialData: {
        users: [],
        total: 0,
        page: 1,
      },
    },
  );

  const profileQuery = useQuery(
    ["adminUserProfile", selectedUserID],
    () => httpFetchUsers(1, 1, "A-Z", undefined, selectedUserID),
    { enabled: Boolean(selectedUserID), retry: false },
  );
  const user = selectedUserID ? profileQuery.data?.users?.[0] : selectedUser;
  const isOpen = selectedUserID ? Boolean(user) : isPanelOpen;
  const refreshProfile = () => {
    refetch();
    if (selectedUserID) profileQuery.refetch();
  };

  const toggleOpen = (u: any) => {
    if (selectedUserID) {
      if (String(u.id) === selectedUserID) {
        setIsOpen(false);
        navigate("/users");
      } else navigate(`/users/${u.id}`);
      return;
    }
    if (isOpen && u.email === user?.email) return setIsOpen(false);

    setUser(u);
    return setIsOpen(true);
  };

  const changeSort = (e: any) => {
    setOrder(e?.target?.value);
  };

  const clearKycReviewStatusFilter = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("kycReviewStatus");
    setSearchParams(nextSearchParams);
    setCurrentPage(1);
  };

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-2xl">Users</h3>
          <div className="w-3/6">
            <Search type="user" />
          </div>
        </div>

        <p className="mt-5 mb-1 text-lg font-bold">Sort</p>
        <Select
          onChange={changeSort}
          value={order}
          data={["A-Z", "Z-A", "Newest to Oldest", "Oldest to Newest"]}
          className="w-full md:w-7/12"
        />
        {kycReviewStatus && (
          <div className="mt-4 inline-flex items-center gap-3 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700">
            <span>{`KYC review: ${kycReviewStatus}`}</span>
            <button
              className="font-medium text-orange-900"
              onClick={clearKycReviewStatusFilter}
              type="button"
            >
              Clear
            </button>
          </div>
        )}

        {selectedUserID && profileQuery.isError && (
          <p role="alert" className="mt-5 text-red-600">
            Unable to load this user profile.
          </p>
        )}
        {selectedUserID && profileQuery.isSuccess && !user && (
          <p role="status" className="mt-5 text-gray-500">
            User not found.
          </p>
        )}
        <div className="flex gap-8 mt-14">
          <div className={`${isOpen ? "w-7/12" : "w-full"} overflow-x-auto`}>
            <Table
              header={[
                "Username",
                "e-mail",
                "Phone number",
                "Market",
                "KYC Tier",
                "OTC Status",
              ]}
              data={[
                ...users?.map((user: any) => [
                  user.userName,
                  user.email,
                  user.phoneNumber,
                  countryLabels[user.country] || user.country || "-",
                  <Badge>{`Tier ${user.level ?? 0}`}</Badge>,
                  <Badge
                    className={statusClassNames[user.otcStatus] || undefined}
                  >
                    {otcStatusLabels[user.otcStatus] || "Not Applied"}
                  </Badge>,
                  () => toggleOpen(user),
                ]),
              ]}
            />

            <Pagination
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              limit={limit}
              page={page}
              total={total}
            />
          </div>
          {isOpen && (
            <UserInfo
              user={user}
              toggle={toggleOpen}
              refetch={refreshProfile}
            />
          )}
        </div>
      </Main>
    </Container>
  );
};
export default Users;
